import { useEffect, useState } from "react";
import "@/App.css";
import { BUSINESS } from "@/data";
import Header from "@/components/Header";
import Hero from "@/components/Hero";
import Services from "@/components/Services";
import RepairPricing from "@/components/RepairPricing";
import WhyChooseUs from "@/components/WhyChooseUs";
import ProductShowcase from "@/components/ProductShowcase";
import PrepaidService from "@/components/PrepaidService";
import SellPhone from "@/components/SellPhone";
import Reviews from "@/components/Reviews";
import FreeDiagnostic from "@/components/FreeDiagnostic";
import StoreVisit from "@/components/StoreVisit";
import FAQ from "@/components/FAQ";
import Footer from "@/components/Footer";
import StickyMobileCTA from "@/components/StickyMobileCTA";
import QuoteDialog from "@/components/QuoteDialog";
import ScreenQuality from "@/components/ScreenQuality";
import MeetPlugMan from "@/components/MeetPlugMan";
import BackGlass from "@/components/BackGlass";

function App() {
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quotePrefill, setQuotePrefill] = useState(null);
  const openQuote = (prefill = null) => {
    setQuotePrefill(prefill);
    setQuoteOpen(true);
  };
  const closeQuote = () => {
    setQuoteOpen(false);
    setQuotePrefill(null);
  };

  // Listen for hash changes (quote link)
  useEffect(() => {
    const handleHash = () => {
      if (window.location.hash === "#quote") {
        setQuoteOpen(true);
        history.replaceState(null, "", window.location.pathname);
      }
    };
    handleHash();
    window.addEventListener("hashchange", handleHash);
    return () => window.removeEventListener("hashchange", handleHash);
  }, []);

  // Inject favicon + LocalBusiness JSON-LD schema
  useEffect(() => {
    document.title = `${BUSINESS.name} | Phone Repair & Unlocked Phones in Miami Gardens`;
    const meta = document.querySelector('meta[name="description"]') || document.createElement("meta");
    meta.setAttribute("name", "description");
    meta.setAttribute(
      "content",
      "Fast iPhone & Samsung repairs, unlocked phones, accessories, prepaid service, and honest tech help in Miami Gardens, FL. 90-day warranty. Free diagnostics. Walk-ins welcome."
    );
    if (!meta.parentNode) document.head.appendChild(meta);

    // Favicon — official logo
    const LOGO_URL = "https://customer-assets.emergentagent.com/job_unlock-miami/artifacts/4opuf54b_png_2.png";
    let favicon = document.querySelector('link[rel="icon"]');
    if (!favicon) {
      favicon = document.createElement("link");
      favicon.rel = "icon";
      document.head.appendChild(favicon);
    }
    favicon.href = LOGO_URL;
    favicon.type = "image/png";

    let appleIcon = document.querySelector('link[rel="apple-touch-icon"]');
    if (!appleIcon) {
      appleIcon = document.createElement("link");
      appleIcon.rel = "apple-touch-icon";
      document.head.appendChild(appleIcon);
    }
    appleIcon.href = LOGO_URL;

    const id = "ld-json-localbusiness";
    let existing = document.getElementById(id);
    if (existing) existing.remove();
    const script = document.createElement("script");
    script.id = id;
    script.type = "application/ld+json";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": ["LocalBusiness", "MobilePhoneStore"],
      name: BUSINESS.name,
      image: "https://images.pexels.com/photos/11457821/pexels-photo-11457821.jpeg",
      "@id": BUSINESS.website,
      url: BUSINESS.website,
      telephone: BUSINESS.phone,
      email: BUSINESS.email,
      priceRange: "$$",
      address: {
        "@type": "PostalAddress",
        streetAddress: "20719 NW 2nd Ave",
        addressLocality: "Miami",
        addressRegion: "FL",
        postalCode: "33169",
        addressCountry: "US",
      },
      geo: { "@type": "GeoCoordinates", latitude: 25.974, longitude: -80.211 },
      openingHoursSpecification: [
        {
          "@type": "OpeningHoursSpecification",
          dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          opens: "10:00",
          closes: "19:00",
        },
      ],
      sameAs: [BUSINESS.instagram],
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: BUSINESS.rating,
        reviewCount: BUSINESS.reviewCount,
      },
    });
    document.head.appendChild(script);
  }, []);

  return (
    <div className="App relative">
      <Header onOpenQuote={openQuote} />
      <main>
        <Hero />
        <Services onOpenQuote={openQuote} />
        <RepairPricing />
        <ScreenQuality />
        <BackGlass onOpenQuote={openQuote} />
        <WhyChooseUs />
        <ProductShowcase />
        <PrepaidService />
        <SellPhone onOpenQuote={openQuote} />
        <Reviews />
        <MeetPlugMan />
        <FreeDiagnostic />
        <StoreVisit />
        <FAQ />
      </main>
      <Footer onOpenQuote={openQuote} />
      <StickyMobileCTA onOpenQuote={openQuote} />
      <QuoteDialog open={quoteOpen} onClose={closeQuote} prefill={quotePrefill} />
    </div>
  );
}

export default App;
